import {
  DunningNotificationTemplates,
  DynamicField,
  ErrorCodeOption,
  RetryIntervalOption,
} from "../interfaces/dunning-settings.interface";

/**
 * Available error codes that can be configured for dunning
 */
export const AVAILABLE_ERROR_CODES: number[] = [
  1000, 1001, 1002, 1003, 1004, 2000, 3000, 3002,
];

/**
 * Error code options for multi-select dropdown
 */
export const ERROR_CODE_OPTIONS: ErrorCodeOption[] = [
  {
    value: 1000,
    label: "Empty Scheduled Order (1000)",
    documentationUrl:
      "https://docs.qpilot.cloud/docs/1000-empty-scheduled-order",
  },
  {
    value: 1001,
    label: "No Items To Ship (1001)",
    documentationUrl: "https://docs.qpilot.cloud/docs/1001-no-items-to-ship",
  },
  {
    value: 1002,
    label: "Shipping Rate Not Found (1002)",
    documentationUrl:
      "https://docs.qpilot.cloud/docs/1002-shipping-rate-not-found",
  },
  {
    value: 1003,
    label: "Payment Integration Not Found (1003)",
    documentationUrl:
      "https://docs.qpilot.cloud/docs/1003-payment-integration-not-found",
  },
  {
    value: 1004,
    label: "No Payment Method (1004)",
    documentationUrl: "https://docs.qpilot.cloud/docs/1004-no-payment-method",
  },
  {
    value: 2000,
    label: "Payment Failed (2000)",
    documentationUrl: "https://docs.qpilot.cloud/docs/2000-payment-failed",
  },
  {
    value: 3000,
    label: "Client Order Creation Failure (3000)",
    documentationUrl:
      "https://docs.qpilot.cloud/docs/3000-client-order-creation-failure",
  },
  {
    value: 3002,
    label: "Client Order Invalid (3002)",
    documentationUrl:
      "https://docs.qpilot.cloud/docs/3002-client-order-creation-invalid-response",
  },
];

/**
 * Allowed retry attempt counts (1-5)
 */
export const ALLOWED_RETRY_ATTEMPTS: number[] = [1, 2, 3, 4, 5];

/**
 * Allowed retry intervals in hours
 */
export const ALLOWED_RETRY_INTERVALS_HOURS: number[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 24, 48, 72, 96,
];

/**
 * Format hours into human-readable retry interval options
 */
export function getRetryIntervalOptions(): RetryIntervalOption[] {
  return ALLOWED_RETRY_INTERVALS_HOURS.map((hours) => ({
    value: hours,
    label: formatRetryInterval(hours),
  }));
}

/**
 * Convert hours to human-readable format
 */
export function formatRetryInterval(hours: number): string {
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (days > 1) return `${days} days`;
  if (days === 1) return "1 day";
  if (remainingHours > 1) return `${remainingHours} hours`;
  return "1 hour";
}

/**
 * Dunning action options for dropdown
 */
export const DUNNING_ACTION_OPTIONS = [
  { value: 0, label: "Nothing" },
  { value: 1, label: "Cancel the Scheduled Order" },
  { value: 2, label: "Skip 1 Cycle" },
];

/**
 * Default notification templates
 */
export const DEFAULT_NOTIFICATION_TEMPLATES: DunningNotificationTemplates = {
  Inactive: null,
  FirstRetry:
    "If no changes are made, we will automatically retry processing your {{ScheduledOrder}} in {{NextDunningHours}} hours.",
  RetryAttempts:
    "If no changes are made, we will automatically retry processing your {{ScheduledOrder}} in {{NextDunningHours}} hours for {{RetryCountLeft}} more attempts.",
  LastRetry:
    "If no changes are made, we will automatically {{DunningAction}} your {{ScheduledOrder}}.",
  Finished: "Your {{ScheduledOrder}} was {{DunningAction}}.",
};

/**
 * Dynamic fields available for dunning notification templates
 */
export const DYNAMIC_FIELDS: DynamicField[] = [
  {
    variable: "{{NextDunningHours}}",
    description:
      "How many hours until the Scheduled Order will be retried unless dunning is cancelled.",
  },
  {
    variable: "{{DunningHours}}",
    description:
      "How often Scheduled Orders in the dunning process will be retried (e.g., every 3 hours).",
  },
  {
    variable: "{{RetryCountDone}}",
    description:
      "How many times the Scheduled Order has been retried in the current dunning process.",
  },
  {
    variable: "{{RetryCountLeft}}",
    description:
      "How many more times the Scheduled Order will be retried unless dunning is cancelled.",
  },
  {
    variable: "{{RetryCount}}",
    description:
      "The total number of times a Scheduled Order can be retried in the dunning process.",
  },
  {
    variable: "{{DunningAction}}",
    description:
      "What action is taken when dunning finishes unsuccessfully (e.g., Skipped or Deleted).",
  },
];

/**
 * LocalStorage key for banner dismissal
 */
export const DUNNING_BANNER_DISMISSED_KEY = "dunningMessageDismissed";

/**
 * External documentation URLs
 */
export const DUNNING_DOCS = {
  workflows: "https://docs.qpilot.cloud/docs/dunning-workflows",
  errorCodes: "https://docs.qpilot.cloud/docs/scheduled-order-failure-codes",
};
